import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input, Alert, Card, CardBody } from 'reactstrap';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { Addendum } from './Contract.interface';
import { format } from 'date-fns';

interface HomeownerReviewModalProps {
  isOpen: boolean;
  toggle: () => void;
  contractId: string;
  addendums: Addendum[];
  onComplete: () => void;
}

const HomeownerReviewModal: React.FC<HomeownerReviewModalProps> = ({
  isOpen,
  toggle,
  contractId,
  addendums,
  onComplete
}) => {
  const [selectedAddendum, setSelectedAddendum] = useState<Addendum | null>(null);
  const [decision, setDecision] = useState<'accept' | 'reject' | ''>('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!selectedAddendum || !decision) return;

    try {
      setIsSubmitting(true);
      const firestore = firebase.firestore();
      const contractRef = firestore.collection('contracts').doc(contractId);

      const response = {
        addendumId: selectedAddendum.date, // Using date as unique identifier
        decision,
        rejectionReason: decision === 'reject' ? rejectionReason : '',
        responseDate: new Date().toISOString(),
      };

      await contractRef.update({
        [`addendums.${addendums.findIndex(a => a.date === selectedAddendum.date)}.response`]: response,
        ...(decision === 'accept' && selectedAddendum.type === 'contractModification' && {
          contractPrice: selectedAddendum.newPrice
        })
      });

      toggle();
      onComplete();
    } catch (error) {
      console.error('Error submitting review:', error);
      setError('Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getLatestAddendum = () => {
    return addendums.reduce((latest, current) => {
      if (!latest) return current;
      return new Date(current.date) > new Date(latest.date) ? current : latest;
    }, null as Addendum | null);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const renderAddendumDetails = (addendum: Addendum) => {
    const formattedDate = format(new Date(addendum.date), 'MMM dd, yyyy h:mm a');
    return (
      <Card className="mb-3">
        <CardBody>
        
          <h5>{addendum.type === 'contingencyRemoval' ? 'Property Inspection Contingency Removal' : 'Contract Modification'}</h5>
          <p className="text-muted mb-2">Submitted on {formattedDate}</p>
          <div className="border-top pt-2 mt-2">
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
              {addendum.content}
            </pre>
            {addendum.type === 'contractModification' && (
              <div className="mt-3">
                <p><strong>Price Change:</strong></p>
                <p>Original Price: {formatPrice(addendum.originalPrice)}</p>
                <p>New Price: {formatPrice(addendum.newPrice)}</p>
                <p>Difference: {formatPrice(addendum.newPrice - addendum.originalPrice)}</p>
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Review Post-Inspection Decision</ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}
        
        {!showConfirmation ? (
          <>
            {!selectedAddendum ? (
              <>
                <h5>Select Addendum to Review</h5>
                {addendums.map((addendum, index) => (
                  <Button
                    key={addendum.date}
                    color="primary"
                    outline
                    className="w-100 mb-2 text-start"
                    onClick={() => setSelectedAddendum(addendum)}
                  >
                    {addendum.type === 'contingencyRemoval' ? 'Contingency Removal' : 'Contract Modification'} - {format(new Date(addendum.date), 'MMM dd, yyyy')}
                  </Button>
                ))}
              </>
            ) : (
              <>
                {renderAddendumDetails(selectedAddendum)}
                
                <FormGroup tag="fieldset">
                  <legend>Your Decision</legend>
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="radio"
                        name="decision"
                        value="accept"
                        checked={decision === 'accept'}
                        onChange={(e) => setDecision(e.target.value as 'accept' | 'reject')}
                      />
                      Accept the {selectedAddendum.type === 'contingencyRemoval' ? 'contingency removal' : 'proposed changes'}
                    </Label>
                  </FormGroup>
                  <FormGroup check>
                    <Label check>
                      <Input
                        type="radio"
                        name="decision"
                        value="reject"
                        checked={decision === 'reject'}
                        onChange={(e) => setDecision(e.target.value as 'accept' | 'reject')}
                      />
                      Reject the {selectedAddendum.type === 'contingencyRemoval' ? 'contingency removal' : 'proposed changes'}
                    </Label>
                  </FormGroup>
                </FormGroup>

                {decision === 'reject' && (
                  <FormGroup>
                    <Label for="rejectionReason">Reason for Rejection</Label>
                    <Input
                      type="textarea"
                      id="rejectionReason"
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      rows={4}
                      required
                    />
                  </FormGroup>
                )}
              </>
            )}
          </>
        ) : (
          <div className="text-center">
            <h4>Confirm Decision</h4>
            <p>
              Are you sure you want to {decision} the {selectedAddendum?.type === 'contingencyRemoval' ? 'contingency removal' : 'proposed changes'}?
              {decision === 'accept' && selectedAddendum?.type === 'contractModification' && (
                <><br /><br />This will update the contract price to {formatPrice(selectedAddendum.newPrice)}.</>
              )}
            </p>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {!showConfirmation ? (
          <>
            {selectedAddendum && (
              <Button color="secondary" onClick={() => setSelectedAddendum(null)}>
                Back to List
              </Button>
            )}
            <Button color="secondary" onClick={toggle}>Cancel</Button>
            {selectedAddendum && (
              <Button
                color="primary"
                onClick={() => setShowConfirmation(true)}
                disabled={!decision || (decision === 'reject' && !rejectionReason)}
              >
                Next
              </Button>
            )}
          </>
        ) : (
          <>
            <Button color="secondary" onClick={() => setShowConfirmation(false)}>Back</Button>
            <Button
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Confirm'}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default HomeownerReviewModal;